import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";

const ChatCard = ({ chat, onSelect }) => {
  const { t } = useTranslation();

  // Format timestamp for display
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    let date = new Date(Number(timestamp));
    if (isNaN(date.getTime())) date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    } else if (diffInHours < 48) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
  };

  const hasUnread = chat.unreadCount > 0;
  const hasPostImage = !!chat.postImageUrl;

  // Get badge config based on post type
  const getTypeBadge = () => {
    switch (chat.postType) {
      case 'lost':
        return { label: t('lost', 'Lost'), className: 'bg-red-100 text-red-700' };
      case 'found':
        return { label: t('found', 'Found'), className: 'bg-green-100 text-green-700' };
      case 'petService':
        return { label: t('pet_service', 'Service'), className: 'bg-purple-100 text-purple-700' };
      case 'psa':
        return { label: t('psa_short', 'PSA'), className: 'bg-blue-100 text-blue-700' };
      default:
        return null;
    }
  };

  const badge = getTypeBadge();

  return (
    <button
      onClick={() => onSelect(chat)}
      className="
        w-full bg-white hover:bg-secondary/50 
        transition-colors duration-200
        p-3 sm:p-4 
        flex gap-2.5 sm:gap-3 items-start 
        text-left
      "
    >
      {/* Post Image or Profile Avatar */}
      <div className="relative flex-shrink-0">
        {hasPostImage ? (
          <img
            src={chat.postImageUrl}
            alt={chat.postName || chat.participant?.username || 'Post'}
            className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg object-cover"
          />
        ) : (
          <div className="
            w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 
            rounded-lg 
            bg-secondary 
            flex items-center justify-center
            overflow-hidden
          ">
            {chat.participant?.avatar ? (
              <img
                src={chat.participant.avatar}
                alt={chat.participant.username}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-xl sm:text-2xl">🐾</span>
            )}
          </div>
        )}
        {/* Post Type Badge */}
        {badge && (
          <span className={`
            absolute -top-1 -right-1 
            text-[8px] sm:text-[9px] 
            px-1 sm:px-1.5 py-0.5 
            rounded-full font-semibold 
            ${badge.className}
          `}>
            {badge.label}
          </span>
        )}
        {/* Unread indicator dot (when no badge, or below badge) */}
        {hasUnread && !badge && (
          <div className="
            absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1
            w-4 h-4 sm:w-5 sm:h-5
            bg-destructive
            text-destructive-foreground
            text-[9px] sm:text-[10px] font-bold
            rounded-full
            flex items-center justify-center
            border-2 border-white
          ">
            {chat.unreadCount > 9 ? '9+' : chat.unreadCount}
          </div>
        )}
      </div>

      {/* Conversation Details */}
      <div className="flex-1 min-w-0">
        {/* Name & Time */}
        <div className="flex items-start justify-between gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
          <div className="flex-1 min-w-0">
            <h3 className={`
              text-xs sm:text-sm md:text-base font-semibold truncate
              ${hasUnread ? 'text-foreground' : 'text-foreground/80'}
            `}>
              {chat.postName 
                ? `${chat.postName} - ${chat.participant?.username || t('unknown_user', 'Unknown User')}`
                : (chat.participant?.username || t('unknown_user', 'Unknown User'))
              }
            </h3>
            {/* Breed subtitle if available */}
            {chat.postBreed && (
              <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
                {chat.postBreed}
              </p>
            )}
          </div>
          <span className="text-[10px] sm:text-xs text-muted-foreground flex-shrink-0">
            {formatTime(chat.updatedAt || chat.createdAt)}
          </span>
        </div>

        {/* Last Message */}
        <p className={`
          text-xs sm:text-sm truncate
          ${hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'}
        `}>
          {chat.lastMessage || t('no_messages_yet_short', 'No messages yet')}
        </p>
      </div>

      {/* Arrow & Unread Indicator */}
      <div className="flex flex-col items-center justify-center gap-1.5 flex-shrink-0 self-center">
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
        {hasUnread && badge && (
          <div className="w-2 h-2 rounded-full bg-primary" />
        )}
      </div>
    </button>
  );
};

export default ChatCard;
